import type { CardsDTO, LanguageDTO } from '@capture/client-api/src/orval'
import { useQuery } from '@tanstack/react-query'
import {
    getGetRequestCardsQueryOptions,
    getGetRequestJobsDefaultQueryOptions,
    getGetRequestJobsInstantsByJobUuidAndDateRangeQueryOptions,
    getGetRequestMemoryCollectionsByCollectionUuidQueryOptions,
    useGetRequestCards,
} from '@capture/client-api/src/orval'
import type {
    CollectionCard,
    CollectionResponse,
    MemoryCard,
} from '~/@types/backend-types'
import { managedPromiseAll } from '~/utilities/promises'
import { getCurrentLangDefinition } from '~/config/constants'
import { buildCommonParams, buildRequestOptions } from './api'
import { queryWithCache } from './queriesWithCache'
import { useAPIConfigContext } from './APIConfigContext'

async function queryCardsWithCache() {
    const { queryKey, queryFn } = getGetRequestCardsQueryOptions(
        buildCommonParams(),
        {
            request: buildRequestOptions('appHost'),
        },
    )
    const response = await queryWithCache({
        queryKey,
        queryFn,
    })

    return response.data
}

export async function queryCollectionAndMemoryCardsWithCache() {
    const cards = await queryCardsWithCache()

    return selectCards(cards)
}

function selectCards(data: CardsDTO) {
    return data.card_sections.flatMap((cardsArr) => cardsArr.cards)
}

function sortSeenOrder(cardA: { seen: boolean }, cardB: { seen: boolean }) {
    return Number(cardA.seen) - Number(cardB.seen)
}

function selectCollectionCards(data: CardsDTO) {
    return selectCards(data)
        .filter((c): c is CollectionCard => Boolean(c.cover_image_url))
        .sort(sortSeenOrder)
}

function selectMemoryCards(data: CardsDTO) {
    return selectCards(data)
        .filter((c): c is MemoryCard => Boolean(!c.cover_image_url))
        .sort(sortSeenOrder)
}

export async function queryCollectionCardsWithCache() {
    const cards = await queryCardsWithCache()

    return selectCollectionCards(cards)
}

export function useCollectionCards() {
    const config = useAPIConfigContext()

    return useGetRequestCards(config.commonParams, {
        query: {
            select: ({ data }) => selectCollectionCards(data),
        },
        request: config.getRequestOptionsForHost('appHost'),
    })
}

export async function queryMemoryCardsWithCache() {
    const cards = await queryCardsWithCache()

    return selectMemoryCards(cards)
}

export async function getQueryMemoryByDateRangeOptions(dateRange: string) {
    const timelinID = await queryTimelineJobIDWithCache()
    const overidedParams = {
        ...buildCommonParams(),
        language: getCurrentLangDefinition().connectKey as LanguageDTO,
    }

    return getGetRequestJobsInstantsByJobUuidAndDateRangeQueryOptions(
        timelinID,
        dateRange,
        overidedParams,
        {
            request: buildRequestOptions('appHost'),
        },
    )
}

export async function queryMemoryByDateRangeWithCache(dateRange: string) {
    const { queryKey, queryFn } =
        await getQueryMemoryByDateRangeOptions(dateRange)
    const response = await queryWithCache({
        queryKey,
        queryFn,
    })

    return response.data
}

export async function queryCollectionByIDWithCache(collectionID: string) {
    const { queryKey, queryFn } =
        getGetRequestMemoryCollectionsByCollectionUuidQueryOptions(
            collectionID,
            buildCommonParams(),
            {
                request: buildRequestOptions('appHost'),
            },
        )
    const response = await queryWithCache({
        queryKey,
        queryFn,
    })

    return response.data
}

export const getCollectionIDFromActionUrl = (actionUrl: string) => {
    return decodeURIComponent(actionUrl).split('memory_collections/')[1]
}

function getMultiGetRequestMemoryCollectionsByCollectionUuidQueryConfig(
    collectionCards: CollectionCard[],
) {
    const collectionIDs = collectionCards.map((c) =>
        getCollectionIDFromActionUrl(c.action_url),
    )

    return {
        queryKey: ['/collections', ...collectionIDs] as any,
        queryFn: () =>
            managedPromiseAll(collectionIDs, async (cID) => {
                return queryCollectionByIDWithCache(cID)
            }),
    }
}

export async function queryCollectionsWithCache() {
    const collectionCards = await queryCollectionCardsWithCache()

    return queryWithCache(
        getMultiGetRequestMemoryCollectionsByCollectionUuidQueryConfig(
            collectionCards,
        ),
    )
}

export function useCollections() {
    const collectionCardsQuery = useCollectionCards()

    return useQuery<CollectionResponse[]>({
        ...getMultiGetRequestMemoryCollectionsByCollectionUuidQueryConfig(
            collectionCardsQuery.data ?? [],
        ),
        enabled: collectionCardsQuery.isSuccess,
    })
}

export async function queryTimelineJobIDWithCache() {
    const { queryKey, queryFn } = getGetRequestJobsDefaultQueryOptions(
        buildCommonParams(),
        {
            request: buildRequestOptions('appHost'),
        },
    )
    const response = await queryWithCache({
        queryKey,
        queryFn,
    })

    return response.data.id
}
